import CheckIcon from "@mui/icons-material/Check";
import CircleIcon from "@mui/icons-material/Circle";
import {
    alpha,
    AppBar,
    Step,
    StepConnector,
    stepConnectorClasses,
    StepIconProps,
    StepLabel,
    Stepper,
    styled,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { routes } from "app/routes";
import { NAVBAR_HEIGHT, NAVBAR_HEIGHT_MOBILE } from "app/styles";
import { push } from "connected-react-router";
import { MENU_SHADOW_HEIGHT } from "features/navigation/TopBarMenu";
import { useDispatch } from "react-redux";
import { NavLink } from "react-router-dom";
import { SignupStep } from "./SignupProgressBar";
import { getCurrentCohort } from "./signupHelpers";
import { PartnerLogo } from "./PartnerLogo";

interface ProgressBarProps {
    currentStep: number;
    onStepClick?: (step: number) => void;
    stepData: { [key: string]: { label: string; route: string } };
    stepOrder: string[];
    nonClickableSteps?: string[];
}

const useStyles = makeStyles((theme) => ({
    checkIcon: {
        fontSize: theme.typography.pxToRem(24),
    },
    circleIcon: {
        fontSize: theme.typography.pxToRem(10),
    },
    stepper: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
        width: "80%",
        margin: "0 auto",
    },
    csStepIconRoot: {
        color: alpha(theme.palette.grey700, 0.8),
    },
    csStepIconActive: {
        color: theme.palette.aqua700,
    },
    csStepIconCompleted: {
        color: theme.palette.aqua700,
    },
    customAppBar: {
        backgroundColor: theme.palette.coral100,
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        minHeight: NAVBAR_HEIGHT,
        overflow: "hidden",

        "&:before": {
            content: "''",
            position: "absolute",
            zIndex: -1,
            left: 0,
            bottom: MENU_SHADOW_HEIGHT,
            top: 0,
            right: 0,
            boxShadow: `${MENU_SHADOW_HEIGHT}px 0 ${MENU_SHADOW_HEIGHT}px ${alpha(
                theme.palette.grey900,
                0.1,
            )}`,
            transition: "opacity 500ms ease",
            opacity: 1,
        },

        [theme.breakpoints.down("lg")]: {
            minHeight: NAVBAR_HEIGHT_MOBILE,
        },
    },
    iconContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: 24,
    },
    smallScreenStepsLabel: {
        color: theme.palette.aqua700,
        marginBottom: 0,
    },
    smallScreenStepsTitle: {
        marginTop: theme.spacing(-1),
        marginBottom: 0,
        color: theme.palette.grey700,
    },
    smallScreenStepsContainer: {
        paddingLeft: theme.spacing(12),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        [theme.breakpoints.down("md")]: {
            paddingLeft: theme.spacing(10),
        },
    },
    logo: {
        width: 60,
        height: 60,
        verticalAlign: "middle",
    },
    logoContainer: {
        display: "flex",
        alignItems: "center",
        position: "absolute",
        top: 0,
        left: 0,
        paddingLeft: theme.spacing(4),
        height: "90%",
        justifyContent: "space-between",
        width: "100%",
    },
    partnerLogo: {
        marginRight: theme.spacing(4),
    },
    altLabel: {
        marginTop: 0,
    },
    stepClickable: {
        cursor: "pointer",
        "&:hover": {
            textDecoration: "underline",
        },
    },
}));

function CsStepIcon(props: StepIconProps) {
    const classes = useStyles();
    const { active, completed } = props;

    return (
        <div
            className={
                completed
                    ? classes.csStepIconCompleted
                    : active
                    ? classes.csStepIconActive
                    : classes.csStepIconRoot
            }
        >
            <div className={classes.iconContainer}>
                {completed ? (
                    <CheckIcon className={classes.checkIcon} />
                ) : (
                    <CircleIcon className={classes.circleIcon} />
                )}
            </div>
        </div>
    );
}

const CsConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: theme.palette.aqua700,
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: theme.palette.aqua700,
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: alpha(theme.palette.grey700, 0.8),
        borderTopWidth: 2,
        borderRadius: 1,
    },
}));

const CsStepLabel = styled(StepLabel)({
    [`& .MuiStepLabel-label.MuiStepLabel-alternativeLabel`]: {
        marginTop: 0,
    },
    [`& .MuiStepLabel-labelContainer`]: {
        width: "fit-content",
    },
});

export const ProgressBar = (props: ProgressBarProps) => {
    const { currentStep, stepData, stepOrder, nonClickableSteps = [] } = props;
    const classes = useStyles();
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
    const dispatch = useDispatch();

    const cohort = getCurrentCohort();

    const MobileProgressBar = () => (
        <AppBar className={classes.customAppBar} position="sticky" elevation={0}>
            <div className={classes.logoContainer}>
                <NavLink to={routes.home()}>
                    <img src={"/images/logo128.png"} className={classes.logo} alt="Logo" />
                </NavLink>
                {cohort && <PartnerLogo cohort={cohort} />}
            </div>
            <div className={classes.smallScreenStepsContainer}>
                <Typography variant="body1" className={classes.smallScreenStepsLabel}>
                    <strong>
                        Step {currentStep + 1} of {stepOrder.length}
                    </strong>
                </Typography>
                <Typography variant="h4" className={classes.smallScreenStepsTitle}>
                    {stepData[stepOrder[currentStep]].label}
                </Typography>
            </div>
        </AppBar>
    );

    return (
        <>
            {isLargeScreen ? (
                <AppBar className={classes.customAppBar} position="sticky" elevation={0}>
                    <div className={classes.logoContainer}>
                        <NavLink to={routes.home()}>
                            <img src={"/images/logo128.png"} className={classes.logo} alt="Logo" />
                        </NavLink>
                        {cohort && <PartnerLogo cohort={cohort} />}
                    </div>

                    <Stepper
                        alternativeLabel
                        activeStep={currentStep}
                        className={classes.stepper}
                        connector={<CsConnector />}
                    >
                        {stepOrder.map((stepKey) => {
                            const step = stepData[stepKey];
                            const isClickable =
                                currentStep > stepOrder.indexOf(stepKey) &&
                                !nonClickableSteps.includes(stepKey);

                            return (
                                <Step key={stepKey}>
                                    <CsStepLabel StepIconComponent={CsStepIcon}>
                                        <span
                                            className={isClickable ? classes.stepClickable : ""}
                                            onClick={(e) => {
                                                if (isClickable) {
                                                    // hacky way to jump back to the rate step - this has to be fixed as soon as the signup flow and its routes is cleaned up
                                                    if (stepKey === SignupStep.RATE) {
                                                        window.location.reload();
                                                    } else {
                                                        dispatch(push(step.route));
                                                    }
                                                }
                                            }}
                                        >
                                            <Typography variant="body1">{step.label}</Typography>
                                        </span>
                                    </CsStepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                </AppBar>
            ) : (
                <MobileProgressBar />
            )}
        </>
    );
};
